import { useEffect, useState } from 'react'

export interface OfflineDetectionState {
  /** True when the browser reports network connectivity. */
  online: boolean
  /** Timestamp of the most recent transition into the current state. */
  since: Date
}

/**
 * Tracks `navigator.onLine` and subscribes to online/offline events.
 * SSR-safe: defaults to `online=true` when `window` is unavailable.
 *
 * The API is intentionally minimal compared to the desktop hook because
 * the PWA has no main-process error queue to reconcile — consumers can
 * layer their own retry logic on top via `useErrorRecovery`.
 */
export function useOfflineDetection(): OfflineDetectionState {
  const [state, setState] = useState<OfflineDetectionState>(() => {
    const online =
      typeof navigator !== 'undefined' ? navigator.onLine !== false : true
    return { online, since: new Date() }
  })

  useEffect(() => {
    if (typeof window === 'undefined') return

    const handleOnline = () => setState({ online: true, since: new Date() })
    const handleOffline = () => setState({ online: false, since: new Date() })

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  return state
}

export default useOfflineDetection
